import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";

import { useAdminData } from "@providers/AdminDataProvider";

// A single "recently opened" entry. We persist ONLY the report id and the local
// view time — never report content. Display data (client name, type, status) is
// re-fetched from the backend via the `getReports` reportIds filter, which is
// org- and role-scoped, so a stale id can never surface another org's PHI.
// `viewedAt` is inherently local (the time *this user* opened the report), not a
// server field, so it lives here rather than in the query.
export interface RecentlyViewedEntry {
  id: string;
  /** ISO timestamp of the most recent open (drives "viewed X ago"). */
  viewedAt: string;
}

const MAX_RECENTLY_VIEWED = 6;

/**
 * Per-user, per-org, localStorage-backed list of recently opened report ids.
 * Keyed by Auth0 subject AND org id so history never bleeds across org contexts.
 * Storing only ids (not report metadata) is the primary isolation guarantee:
 * even if an id from another org were somehow present, the org- and role-scoped
 * `getReports` reportIds filter returns nothing for it. The org-scoped key is
 * kept as defense-in-depth. `recordView` is called at every report-open site;
 * the widget reads `recentlyViewed`. usehooks-ts syncs instances in the same tab,
 * so opening a report and returning Home reflects the new entry immediately.
 */
export const useRecentlyViewedReports = () => {
  const { user } = useAuth0();
  const { orgId } = useAdminData();

  // Scope by both user sub and org id — user sub alone is insufficient because a
  // user with multi-org access would carry ids from a prior org's reports.
  const storageKey = `recently-viewed-reports:${user?.sub ?? "anon"}:${orgId ?? "no-org"}`;
  const [recentlyViewed, setRecentlyViewed] = useLocalStorage<RecentlyViewedEntry[]>(storageKey, []);

  const recordView = useCallback(
    (reportId: string) => {
      // Don't write until the org is confirmed — avoids persisting to an
      // unscoped key that could surface in a different org context later.
      if (!orgId) return;
      setRecentlyViewed((prev) => {
        // Move-to-front: drop any prior entry for this report, prepend the fresh
        // one, then cap so the list stays a tight "recent" set.
        const withoutCurrent = prev.filter((r) => r.id !== reportId);
        return [{ id: reportId, viewedAt: new Date().toISOString() }, ...withoutCurrent].slice(0, MAX_RECENTLY_VIEWED);
      });
    },
    [orgId, setRecentlyViewed],
  );

  // Suppress entries until the org is confirmed — prevents briefly surfacing
  // stale ids from a prior org's key while orgId is still resolving.
  return { recentlyViewed: orgId ? recentlyViewed : [], recordView };
};
